import {
  animalApiIds,
  type BenchlingWorklistSummary,
  describeWorklistItems,
  sampleApiIds,
} from '@/components/Modals/CreateBenchlingWorklist/CreateBenchlingWorklist.utils';
import Radio from '@/components/UI/FormElements/Radio/Radio';
import Select from '@/components/UI/Select/Select';
import { successToast } from '@/helpers';
import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { Sample } from '@/model/Sample.model';
import type { StudyApiId } from '@/model/Study.model';
import { BenchlingDelegatedIcon } from '@/support/benchlingLink/BenchlingDelegatedIcon';
import { BENCHLING_DELEGATED_TOOLTIP } from '@/support/benchlingLink/BenchlingLink.utils';
import { useBenchlingDelegatedCall } from '@/support/benchlingLink/useBenchlingDelegatedCall';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, type FormEvent, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

export interface CreateBenchlingWorklistProps {
  studyApiId: StudyApiId;
  /** Either or both may be given; animals are listed first in the worklist, then samples. */
  animals?: Array<Animal>;
  samples?: Array<Sample>;
  closeModal: () => void;
  /** Runs once a worklist has been created or added to; the tables use it to clear their selection. */
  onSuccess?: () => void;
}

interface BenchlingWorklistFormData {
  name: string;
}

type WorklistMode = 'create' | 'add';

const CreateBenchlingWorklist: FC<CreateBenchlingWorklistProps> = ({
  studyApiId,
  animals = [],
  samples = [],
  closeModal,
  onSuccess,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<BenchlingWorklistFormData>({
    mode: 'onChange',
    defaultValues: { name: '' },
  });

  const [mode, setMode] = useState<WorklistMode>('create');
  const [worklists, setWorklists] = useState<Array<BenchlingWorklistSummary>>();
  const [target, setTarget] = useState<BenchlingWorklistSummary | null>(null);
  const [listFailure, setListFailure] = useState<'retryable' | 'final'>();

  const done = useRef(false);
  const submitting = useRef(false);

  const createWorklist = useBenchlingDelegatedCall({
    endpoint: 'POST /api/v1/studies/{studyApiId}/benchling-worklists',
    actionLabel: 'create this worklist',
    slug: 'benchling-worklist-create',
    // Nothing in this modal can help until the user has linked, so get it out of their way.
    onFailure: { benchling_link_required: closeModal },
  });
  const listWorklists = useBenchlingDelegatedCall({
    endpoint: 'GET /api/v1/studies/{studyApiId}/benchling-worklists',
    actionLabel: 'list your worklists',
    slug: 'benchling-worklist-list',
    onFailure: { benchling_link_required: closeModal },
  });
  const addItems = useBenchlingDelegatedCall({
    endpoint: 'POST /api/v1/studies/{studyApiId}/benchling-worklists/{benchlingWorklistId}/items',
    actionLabel: 'add to this worklist',
    slug: 'benchling-worklist-items-add',
    onFailure: { benchling_link_required: closeModal },
  });

  const selection = {
    ...(_isNotEmpty(animals) ? { animal_api_ids: animalApiIds(animals) } : {}),
    ...(_isNotEmpty(samples) ? { sample_api_ids: sampleApiIds(samples) } : {}),
  };
  // item_count is the number this request put in, never the worklist's total, so the noun can come from the selection.
  const itemNoun =
    _isNotEmpty(animals) && _isNotEmpty(samples) ? 'items' : _isNotEmpty(samples) ? 'samples' : 'animals';

  // Fetched on first use rather than on open: most users create, and listing is a Benchling call made
  // under their name that shares the tenant's rate limit with sync.
  const loadWorklists = async (): Promise<void> => {
    if (worklists !== undefined || listWorklists.loading) {
      return;
    }
    const result = await listWorklists.invoke({ path: { studyApiId } });
    if (result.ok) {
      setWorklists(result.body);
      setListFailure(undefined);
    } else {
      // An unclassified failure carries no retryable flag, so it is offered a retry rather than ruled out.
      setListFailure(result.failure?.retryable === false ? 'final' : 'retryable');
    }
  };

  const chooseMode = async (next: WorklistMode): Promise<void> => {
    setMode(next);
    if (next === 'add') {
      await loadWorklists();
    }
  };

  /** Runs one submission at a time, and none after one has succeeded; `send` reports success. */
  const submitOnce = async (send: () => Promise<boolean>): Promise<void> => {
    if (done.current || submitting.current) {
      return;
    }
    submitting.current = true;
    try {
      done.current = await send();
    } finally {
      submitting.current = false;
    }
  };

  const onCreate = (data: BenchlingWorklistFormData): Promise<void> =>
    submitOnce(async () => {
      const result = await createWorklist.invoke({
        path: { studyApiId },
        body: { name: data.name.trim(), ...selection },
      });
      if (result.ok) {
        successToast(`Created Benchling worklist "${result.body.name}" with ${result.body.item_count} ${itemNoun}`);
        closeModal();
        onSuccess?.();
      }
      return result.ok;
    });

  const onAdd = (): Promise<void> =>
    submitOnce(async () => {
      if (!_notNil(target)) {
        return false;
      }
      const result = await addItems.invoke({
        path: { studyApiId, benchlingWorklistId: target.benchling_worklist_id },
        body: selection,
      });
      if (result.ok) {
        successToast(`Added ${result.body.item_count} ${itemNoun} to Benchling worklist "${result.body.name}"`);
        closeModal();
        onSuccess?.();
      }
      return result.ok;
    });

  // Each mode validates only its own field: the name stays registered while hidden, so routing an add
  // through handleSubmit would fail on a name the user was never asked for.
  const onSubmit = (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    return mode === 'create' ? handleSubmit(onCreate)() : onAdd();
  };

  const fieldError = errors.name?.message;
  const loading = createWorklist.loading || addItems.loading;
  const canSubmit = mode === 'create' ? isValid : _notNil(target);

  return (
    <ModalContainer size="narrow" testKey="create-benchling-worklist">
      <ModalHeader
        title={
          <>
            <BenchlingDelegatedIcon className="mr2" testId="create-benchling-worklist__delegated-icon" />
            Benchling worklist
          </>
        }
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
      />

      {/* Outside the form: the mode is not submitted data, and keeping the radios out leaves the name as
          the form's only input, so Enter in it submits. */}
      <div className="ph3 pt3">
        {/* The same sentence as the title logo's tooltip, so the two cannot drift apart. */}
        <p className="mb3 f6 mid-gray">
          {describeWorklistItems(animals.length, samples.length)} will be added. {BENCHLING_DELEGATED_TOOLTIP}
        </p>

        <div className="mb3" role="radiogroup" aria-label="Worklist">
          <Radio
            id="benchling-worklist-mode-create"
            name="benchling-worklist-mode"
            value="create"
            checked={mode === 'create'}
            onChange={() => chooseMode('create')}
            disabled={loading}
            label="Create new worklist"
            sublabel="Start a new worklist containing only this selection."
            keyboardFocusable
            className="mb2"
            dataTestId="create-benchling-worklist__mode-create"
          />
          <Radio
            id="benchling-worklist-mode-add"
            name="benchling-worklist-mode"
            value="add"
            checked={mode === 'add'}
            onChange={() => chooseMode('add')}
            disabled={loading}
            label="Add to existing worklist"
            sublabel="Append this selection to a worklist you already have. Nothing is removed."
            keyboardFocusable
            dataTestId="create-benchling-worklist__mode-add"
          />
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <div className="ph3 pb3">
          {mode === 'create' ? (
            <>
              <label htmlFor="benchling-worklist-name" className="db mb1 f6">
                Worklist name
              </label>
              <input
                id="benchling-worklist-name"
                type="text"
                className={_isNotBlank(fieldError) ? 'input__error' : ''}
                placeholder="Enter a name for this worklist..."
                autoFocus
                data-testid="create-benchling-worklist__name"
                {...register('name', {
                  validate: (value) => (_isNotBlank(value?.trim()) ? true : 'A worklist name is required.'),
                })}
              />
              {_isNotBlank(fieldError) && (
                <small className="red db" data-testid="create-benchling-worklist__error">
                  {fieldError}
                </small>
              )}
            </>
          ) : (
            <div data-testid="create-benchling-worklist__target">
              <label className="db mb1 f6">Worklist</label>
              <Select<BenchlingWorklistSummary>
                className="w-100"
                aria-label="Worklist"
                isMulti={false}
                isSearchable
                isClearable
                isLoading={listWorklists.loading}
                options={worklists ?? []}
                value={target}
                onChange={(next) => setTarget(next ?? null)}
                getOptionLabel={(worklist) => worklist.name ?? 'Untitled worklist'}
                getOptionValue={(worklist) => worklist.benchling_worklist_id}
                // The default filter also matches the option's value, which here is the Benchling id.
                filterOption={({ label }, inputValue) =>
                  label.toLocaleLowerCase().includes(inputValue.trim().toLocaleLowerCase())
                }
                placeholder="Search your recent worklists..."
                onMenuOpen={listFailure === 'retryable' ? loadWorklists : undefined}
                noOptionsMessage={({ inputValue }) => {
                  if (listFailure === 'retryable') {
                    return 'Could not load your worklists. Open the list again to retry.';
                  }
                  if (listFailure === 'final') {
                    return 'Could not load your worklists.';
                  }
                  return _isNotBlank(inputValue) ? 'No recent worklist matches that name' : 'No entity worklists found';
                }}
                menuPortalTarget={document.body}
              />
              <small className="db mt1 mid-gray">Showing your 100 most recently modified worklists.</small>
            </div>
          )}
        </div>

        <ModalActions
          submitBtnText={mode === 'create' ? 'Create worklist' : 'Add to worklist'}
          submitButtonProps={{ disabled: loading || !canSubmit, loading }}
          onSubmit={onSubmit}
          onCancel={closeModal}
          className="pa3 bt b--moon-gray"
        />
      </form>
    </ModalContainer>
  );
};

export default CreateBenchlingWorklist;
